import { ApolloClient, createHttpLink, from, InMemoryCache, NormalizedCacheObject, ServerError } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ASTNode, stripIgnoredCharacters } from 'graphql';
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { Log } from '@/logging/datadog';
/**
 * Helper function to create Apollo GraphQL client. Note that this function
 * should only be used to create a client on the Node server and should not
 * be used for client-side GraphQL queries
 * @param uri GraphQL endpoint URL
 * @param token Token that is placed in the HTTP header of the query
 * @returns ApolloClient instance
 */
export function createApolloClient(uri: string, token: string | null): ApolloClient<NormalizedCacheObject> {
  // Log errors but don't handle retries here
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        const errorInfo = `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations
        )}, Path: ${JSON.stringify(path)}`;
        Log.info(errorInfo);
      });
    }
    if (networkError) {
      Log.info(`[Network error]: ${networkError}`);
    }
  });

  // Configure RetryLink for smart retry behavior
  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 20000,
    },
    attempts: {
      max: 5,
      retryIf: (error, _operation) => {
        // Only retry on 429 Too Many Requests errors
        if (!error || !('statusCode' in error) || error.statusCode !== 429) {
          return false; // Don't retry other errors
        }
        const serverError = error as ServerError;
        if (serverError.response) {
          // Get the Contentful-specific retry value
          const retryAfter = serverError.response.headers.get('X-Contentful-RateLimit-Reset');
          if (retryAfter) {
            // Override the default delay with the Contentful-provided value
            // Note: RetryLink calculates its own backoff, but this will respect Contentful's header
            const delayMs = Math.max(parseInt(retryAfter, 10) || 1, 1) * 1000;

            // We're returning true to signal we want to retry,
            // but we're also using the delay to align with Contentful's rate limits
            return new Promise((resolve) => {
              setTimeout(() => resolve(true), delayMs);
            });
          }
        }
        return true; // Still retry even if we couldn't get a specific reset time
      },
    },
  });

  const httpLink = createHttpLink({
    uri,
    print(ast: ASTNode, originalPrint: (ast: ASTNode) => string) {
      return stripIgnoredCharacters(originalPrint(ast));
    },
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  return new ApolloClient({
    link: from([errorLink, retryLink, authLink.concat(httpLink)]),
    cache: new InMemoryCache(),
    devtools: {
      enabled: process.env.NODE_ENV?.toLowerCase() !== 'production',
    },
  });
}

// Apollo client for the search API
export function createApolloClientForSearch(): ApolloClient<NormalizedCacheObject> {
  const url = process.env.NEXT_PUBLIC_SEARCH_ENDPOINT || '';
  const httpLink = createHttpLink({
    uri: url,
  });

  const authLink = createAuthLink({
    url: url,
    region: 'eu-west-1',
    auth: {
      type: AUTH_TYPE.AWS_IAM,
      credentials: async () => {
        return {
          accessKeyId: process.env.NEXT_PUBLIC_SEARCH_ACCESS_KEY_ID || '',
          secretAccessKey: process.env.NEXT_PUBLIC_SEARCH_ACCESS_KEY || '',
        };
      },
    },
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
    devtools: {
      enabled: process.env.NODE_ENV?.toLowerCase() !== 'production',
    },
  });
}

// Apollo client for the search API
export function createApolloClientForProxyApi(token: string | undefined): ApolloClient<NormalizedCacheObject> {
  const url = process.env.API_PROXY_GRAPH_URL || '';
  const gatewayKey = process.env.GATEWAY_API_KEY || '';

  const httpLink = createHttpLink({ uri: url });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        'x-api-key': gatewayKey,
        'apollographql-client-name': 'order-web',
        'apollographql-client-version': '1.0',
      },
    };
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
    name: 'order-web',
    version: '1.0',
    devtools: {
      enabled: process.env.NODE_ENV?.toLowerCase() !== 'production',
    },
  });
}
